import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import type { SerializedColumnFilters } from '../../contracts/grid-filter-descriptor.interface';
import type { GridRow } from '../../contracts/grid-row.type';
import type {
  GridHeaderSelectionState,
  GridRowKeyFn,
  GridRowSelectionMode,
  GridSelectionDescriptor,
  GridSelectionScope,
  GridSelectionType
} from '../../contracts/grid-row-selection.interface';
import {
  applySelectionMode,
  computeGridMultiSelection,
  isPathCoveredByScope,
  type GridSelectionModifiers
} from './data-grid-selection.utils';

/** Query identity the descriptor snapshots alongside the selected keys. */
export interface DataGridSelectionQueryContext {
  /** Current group-by chain. */
  readonly groupByFields: ReadonlyArray<string>;
  /** Serialised column filters. */
  readonly columnFilters: SerializedColumnFilters;
  /** Server-reported total matching the live query. */
  readonly totalCount: number;
}

/**
 * Per-grid selection state. Provided on `DeightonDataGridComponent` so recursive
 * group-tree leaves can inject it without extra input threading.
 */
@Injectable()
export class DataGridSelectionStore {
  /** Active selection mode. `none` makes every mutator a no-op. */
  public readonly mode: WritableSignal<GridRowSelectionMode> = signal<GridRowSelectionMode>('none');

  /** Keys the user explicitly included. */
  public readonly includedKeys: WritableSignal<ReadonlySet<string>> = signal<ReadonlySet<string>>(
    new Set<string>()
  );

  /** Keys deselected inside an allMatching or scope selection. */
  public readonly excludedKeys: WritableSignal<ReadonlySet<string>> = signal<ReadonlySet<string>>(
    new Set<string>()
  );

  /** Whether every row matching the live query is selected. */
  public readonly allMatching: WritableSignal<boolean> = signal<boolean>(false);

  /** Group subtrees selected wholesale. */
  public readonly scopes: WritableSignal<ReadonlyArray<GridSelectionScope>> = signal<
    ReadonlyArray<GridSelectionScope>
  >(new Array<GridSelectionScope>());

  /** Group subtrees removed from an allMatching selection, including unfetched rows. */
  public readonly excludedScopes: WritableSignal<ReadonlyArray<GridSelectionScope>> = signal<
    ReadonlyArray<GridSelectionScope>
  >(new Array<GridSelectionScope>());

  /** Materialised rows keyed by selection identity, surviving virtual-scroll recycling. */
  private readonly rowCache: WritableSignal<ReadonlyMap<string, GridRow>> = signal<
    ReadonlyMap<string, GridRow>
  >(new Map<string, GridRow>());

  /** Shift-range anchors keyed by leaf `sortPath`. */
  private readonly anchors: Map<string, number> = new Map<string, number>();

  /** Live query identity copied into the descriptor. */
  private readonly queryContext: WritableSignal<DataGridSelectionQueryContext> =
    signal<DataGridSelectionQueryContext>({
      groupByFields: new Array<string>(),
      columnFilters: new Array<SerializedColumnFilters[number]>(),
      totalCount: 0
    });

  /** Row-key resolver configured by the host grid. */
  private rowKeyFn: GridRowKeyFn = (row: GridRow): string => {
    const id: unknown = row['id'];
    if (typeof id === 'string' && id.length > 0) {
      return id;
    }
    const key: unknown = row['key'];
    return typeof key === 'number' ? String(key) : '';
  };

  /** Estimated number of selected rows, including unfetched scoped / allMatching rows. */
  public readonly count: Signal<number> = computed((): number => this.descriptor().estimatedCount);

  /** Cardinality used to gate toolbar actions. */
  public readonly selectionType: Signal<GridSelectionType> = computed((): GridSelectionType => {
    const count: number = this.count();
    if (count <= 0) {
      return 'none';
    }
    if (count === 1) {
      return 'single';
    }
    return 'multi';
  });

  /** Snapshot consumed by `selectionChange` and action handlers. */
  public readonly descriptor: Signal<GridSelectionDescriptor> = computed((): GridSelectionDescriptor => {
    const included: ReadonlySet<string> = this.includedKeys();
    const excluded: ReadonlySet<string> = this.excludedKeys();
    const scopes: ReadonlyArray<GridSelectionScope> = this.scopes();
    const excludedScopes: ReadonlyArray<GridSelectionScope> = this.excludedScopes();
    const cache: ReadonlyMap<string, GridRow> = this.rowCache();
    const context: DataGridSelectionQueryContext = this.queryContext();
    const allMatching: boolean = this.allMatching();

    const rows: Array<GridRow> = new Array<GridRow>();
    for (const key of included) {
      if (excluded.has(key)) {
        continue;
      }
      const row: GridRow | undefined = cache.get(key);
      if (row !== undefined) {
        rows.push(row);
      }
    }

    let estimatedCount: number;
    if (allMatching) {
      const excludedScopeTotal: number = this.scopeCountTotal(excludedScopes);
      const includedScopeUnderExclusion: number = this.scopeCountTotal(
        scopes.filter((scope: GridSelectionScope): boolean =>
          excludedScopes.some((excludedScope: GridSelectionScope): boolean =>
            isPathCoveredByScope(scope.path, excludedScope.path)
          )
        )
      );
      estimatedCount = Math.max(
        0,
        context.totalCount - excluded.size - excludedScopeTotal + included.size + includedScopeUnderExclusion
      );
    } else if (scopes.length > 0) {
      estimatedCount = Math.max(0, this.scopeCountTotal(scopes) - excluded.size + included.size);
    } else {
      estimatedCount = included.size;
    }

    return {
      mode: allMatching ? 'allMatching' : 'explicit',
      rows,
      includedKeys: [...included],
      excludedKeys: [...excluded],
      scopes: [...scopes],
      excludedScopes: [...excludedScopes],
      groupByFields: [...context.groupByFields],
      columnFilters: context.columnFilters,
      estimatedCount
    };
  });

  /**
   * Grid-wide tri-state for the grouped select-all checkbox.
   * `all` when every matching row is selected (allMatching with no exclusions, or
   * estimated count covering the query total); `some` when a partial selection exists.
   */
  public readonly globalSelectionState: Signal<GridHeaderSelectionState> = computed(
    (): GridHeaderSelectionState => {
      if (this.mode() === 'none') {
        return 'none';
      }
      if (this.allMatching()) {
        if (this.excludedKeys().size === 0 && this.excludedScopes().length === 0) {
          return 'all';
        }
        return this.count() <= 0 ? 'none' : 'some';
      }
      const selectedCount: number = this.count();
      const total: number = this.queryContext().totalCount;
      if (selectedCount <= 0) {
        return 'none';
      }
      if (total > 0 && selectedCount >= total) {
        return 'all';
      }
      return 'some';
    }
  );

  /**
   * Initializes DataGridSelectionStore.
   */
  constructor() {}

  /**
   * Sets the active selection mode. Switching to `none` or `single` collapses state.
   *
   * @param mode - New mode.
   */
  public setMode(mode: GridRowSelectionMode): void {
    const previous: GridRowSelectionMode = this.mode();
    this.mode.set(mode);
    if (mode === 'none') {
      this.clear();
      return;
    }
    if (mode === 'single' && previous !== 'single') {
      this.collapseToSingle();
    }
  }

  /**
   * Installs the row-key resolver used by every mutator.
   *
   * @param rowKeyFn - Function that returns a stable key, or an empty string when unresolved.
   */
  public setRowKeyResolver(rowKeyFn: GridRowKeyFn): void {
    this.rowKeyFn = rowKeyFn;
  }

  /**
   * Updates the query identity copied into the descriptor. Does not clear selection;
   * the host grid decides when to call {@link clear}.
   *
   * @param context - Live group-by, filters, and total.
   */
  public setQueryContext(context: DataGridSelectionQueryContext): void {
    this.queryContext.set(context);
  }

  /**
   * Drops explicit keys that disappeared from the overlapping loaded window
   * while keeping keys that only lived on pages that unloaded after a reset.
   * An empty `nextRows` means the loaded window is gone; every previous-window
   * key is stale. Callers must not pass a transient clear (use the previous
   * window until the next non-empty page).
   *
   * @param previousRows - Loaded rows before the latest data input.
   * @param nextRows - Loaded rows after the latest data input.
   */
  public reconcileLoadedRows(previousRows: ReadonlyArray<GridRow>, nextRows: ReadonlyArray<GridRow>): void {
    if (this.mode() === 'none') {
      return;
    }
    const staleKeys: ReadonlySet<string> = this.collectStaleKeysFromLoadedWindow(previousRows, nextRows);
    if (staleKeys.size === 0) {
      return;
    }
    this.dropKeys(staleKeys);
  }

  /**
   * Resolves a row's selection key.
   *
   * @param row - Data row.
   * @returns Key, or null when unresolved.
   */
  public keyOf(row: GridRow): string | null {
    const key: string = this.rowKeyFn(row);
    return key.length > 0 ? key : null;
  }

  /**
   * Whether a key is currently selected.
   *
   * @param key - Row key.
   * @param path - Optional leaf path used to honour group scopes.
   * @returns True when the row is selected.
   */
  public isSelected(key: string, path?: string): boolean {
    if (this.excludedKeys().has(key)) {
      return false;
    }
    if (this.allMatching()) {
      if (path !== undefined && this.isPathInExcludedScopes(path)) {
        if (this.isPathInScopes(path)) {
          return true;
        }
        return this.includedKeys().has(key);
      }
      return true;
    }
    if (this.includedKeys().has(key)) {
      return true;
    }
    if (path !== undefined && this.isPathInScopes(path)) {
      return true;
    }
    return false;
  }

  /**
   * Header / group checkbox tri-state for a table or group path.
   *
   * @param path - Flat sentinel or composite group path.
   * @param loadedRows - Rows currently rendered for this path.
   * @returns `none`, `some`, or `all`.
   */
  public headerState(path: string, loadedRows: ReadonlyArray<GridRow>): GridHeaderSelectionState {
    if (this.mode() === 'none') {
      return 'none';
    }
    if (this.allMatching()) {
      return this.allMatchingHeaderState(path, loadedRows);
    }
    if (this.isPathInScopes(path)) {
      return this.hasExclusionInRows(loadedRows) ? 'some' : 'all';
    }
    if (loadedRows.length === 0) {
      return this.includedKeys().size > 0 ? 'some' : 'none';
    }
    let selectedCount: number = 0;
    for (const row of loadedRows) {
      const key: string | null = this.keyOf(row);
      if (key !== null && this.includedKeys().has(key) && !this.excludedKeys().has(key)) {
        selectedCount += 1;
      }
    }
    if (selectedCount === 0) {
      return 'none';
    }
    if (selectedCount === loadedRows.length) {
      return 'all';
    }
    return 'some';
  }

  /**
   * Applies Drive/OneDrive click semantics to a leaf table.
   *
   * @param items - Ordered rows currently displayed in the table.
   * @param clickedIndex - Index of the clicked row.
   * @param modifiers - Keyboard modifiers.
   * @param path - Leaf `sortPath`, used as the Shift anchor key.
   */
  public applyClick(
    items: ReadonlyArray<GridRow>,
    clickedIndex: number,
    modifiers: GridSelectionModifiers,
    path: string
  ): void {
    if (this.mode() === 'none') {
      return;
    }
    if (clickedIndex < 0 || clickedIndex >= items.length) {
      return;
    }

    if (this.mode() === 'single') {
      this.replaceWith([items[clickedIndex]]);
      this.setAnchor(path, clickedIndex);
      return;
    }

    const isCtrlOrCmd: boolean = modifiers.ctrlKey || modifiers.metaKey;
    const clicked: GridRow = items[clickedIndex];
    const clickedKey: string | null = this.keyOf(clicked);

    if (!modifiers.shiftKey && isCtrlOrCmd && clickedKey !== null && this.isCoveredSelection(path)) {
      this.toggleExclusionOrInclusion(clicked, path);
      this.setAnchor(path, clickedIndex);
      return;
    }

    const current: Array<GridRow> = this.materialisedRows(items, path);
    const result = computeGridMultiSelection(
      items,
      clickedIndex,
      modifiers,
      current,
      this.anchorFor(path),
      (row: GridRow): string => this.keyOf(row) ?? ''
    );
    this.replaceWith(applySelectionMode(result.newSelection, this.mode()));
    this.setAnchor(path, result.newLastIndex);
  }

  /**
   * Toggles a row checkbox without applying plain-click replacement.
   *
   * @param row - Data row.
   * @param path - Leaf path.
   */
  public toggleRow(row: GridRow, path: string): void {
    if (this.mode() === 'none') {
      return;
    }
    if (this.mode() === 'single') {
      const key: string | null = this.keyOf(row);
      if (key !== null && this.isSelected(key, path)) {
        this.clear();
        return;
      }
      this.replaceWith([row]);
      return;
    }
    this.toggleExclusionOrInclusion(row, path);
  }

  /**
   * Replaces the explicit selection with the given rows and clears covering modes.
   *
   * @param rows - Rows that become the entire selection.
   */
  public replaceWith(rows: ReadonlyArray<GridRow>): void {
    if (this.mode() === 'none') {
      return;
    }
    const nextRows: ReadonlyArray<GridRow> = applySelectionMode(rows, this.mode());
    const nextKeys: Set<string> = new Set<string>();
    const cache: Map<string, GridRow> = new Map<string, GridRow>(this.rowCache());
    for (const row of nextRows) {
      const key: string | null = this.keyOf(row);
      if (key === null) {
        continue;
      }
      nextKeys.add(key);
      cache.set(key, row);
    }
    this.includedKeys.set(nextKeys);
    this.excludedKeys.set(new Set<string>());
    this.allMatching.set(false);
    this.scopes.set(new Array<GridSelectionScope>());
    this.excludedScopes.set(new Array<GridSelectionScope>());
    this.rowCache.set(cache);
  }

  /**
   * Selects every loaded row in a table. Used by the header checkbox and Ctrl/Cmd+A.
   *
   * @param rows - Currently loaded rows for the table.
   */
  public selectAllLoaded(rows: ReadonlyArray<GridRow>): void {
    if (this.mode() === 'none') {
      return;
    }
    if (this.mode() === 'single') {
      if (rows.length > 0) {
        this.replaceWith([rows[0]]);
      }
      return;
    }
    const nextKeys: Set<string> = new Set<string>(this.includedKeys());
    const cache: Map<string, GridRow> = new Map<string, GridRow>(this.rowCache());
    for (const row of rows) {
      const key: string | null = this.keyOf(row);
      if (key === null) {
        continue;
      }
      nextKeys.add(key);
      cache.set(key, row);
    }
    const nextExcluded: Set<string> = new Set<string>(this.excludedKeys());
    for (const key of nextKeys) {
      nextExcluded.delete(key);
    }
    this.includedKeys.set(nextKeys);
    this.excludedKeys.set(nextExcluded);
    this.rowCache.set(cache);
  }

  /**
   * Deselects the loaded rows in a table (header uncheck).
   *
   * @param path - Table path.
   * @param rows - Currently loaded rows.
   */
  public deselectAllLoaded(path: string, rows: ReadonlyArray<GridRow>): void {
    if (this.mode() === 'none') {
      return;
    }
    if (this.allMatching()) {
      this.clear();
      return;
    }
    if (this.isPathInScopes(path)) {
      this.removeCoveringScopes(path);
      return;
    }
    const remove: Set<string> = new Set<string>();
    for (const row of rows) {
      const key: string | null = this.keyOf(row);
      if (key !== null) {
        remove.add(key);
      }
    }
    const nextKeys: Set<string> = new Set<string>(
      [...this.includedKeys()].filter((key: string): boolean => !remove.has(key))
    );
    this.includedKeys.set(nextKeys);
  }

  /**
   * Toggles the header checkbox for a table.
   *
   * @param path - Table path.
   * @param rows - Currently loaded rows.
   */
  public toggleSelectAllLoaded(path: string, rows: ReadonlyArray<GridRow>): void {
    const state: GridHeaderSelectionState = this.headerState(path, rows);
    if (state === 'all') {
      this.deselectAllLoaded(path, rows);
      return;
    }
    this.selectAllLoaded(rows);
  }

  /**
   * Selects every row matching the live query, including unfetched pages.
   */
  public selectAllMatching(): void {
    if (this.mode() !== 'multiple') {
      return;
    }
    this.allMatching.set(true);
    this.includedKeys.set(new Set<string>());
    this.excludedKeys.set(new Set<string>());
    this.scopes.set(new Array<GridSelectionScope>());
    this.excludedScopes.set(new Array<GridSelectionScope>());
  }

  /**
   * Toggles the grouped select-all checkbox between all-matching and clear.
   */
  public toggleSelectAllMatching(): void {
    if (this.globalSelectionState() === 'all') {
      this.clear();
      return;
    }
    this.selectAllMatching();
  }

  /**
   * Toggles a group-level scope selection.
   *
   * When every matching row is selected, the group is added to or removed from
   * `excludedScopes` so sibling groups stay selected. Otherwise the group is
   * added to or removed from the included `scopes` list.
   *
   * @param scope - Group path, field, and estimated count.
   * @param loadedRows - Currently loaded rows in the group, used to prune per-row exceptions.
   */
  public toggleScope(
    scope: GridSelectionScope,
    loadedRows: ReadonlyArray<GridRow> = new Array<GridRow>()
  ): void {
    if (this.mode() !== 'multiple') {
      return;
    }
    if (this.allMatching()) {
      this.toggleScopeWhileAllMatching(scope, loadedRows);
      return;
    }
    if (this.hasScope(scope.path)) {
      this.removeCoveringScopes(scope.path);
      return;
    }
    const next: Array<GridSelectionScope> = this.scopes().filter(
      (existing: GridSelectionScope): boolean => !isPathCoveredByScope(existing.path, scope.path)
    );
    next.push(scope);
    this.scopes.set(next);
    this.excludedScopes.set(new Array<GridSelectionScope>());
    this.allMatching.set(false);
  }

  /**
   * Shift-range anchor for a leaf path.
   *
   * @param path - Leaf `sortPath`.
   * @returns Stored index, or null.
   */
  public anchorFor(path: string): number | null {
    const anchor: number | undefined = this.anchors.get(path);
    return anchor === undefined ? null : anchor;
  }

  /**
   * Stores the Shift-range anchor for a leaf path.
   *
   * @param path - Leaf `sortPath`.
   * @param index - Clicked index.
   */
  public setAnchor(path: string, index: number): void {
    this.anchors.set(path, index);
  }

  /**
   * Clears all selection state, including covering modes and anchors.
   */
  public clear(): void {
    this.includedKeys.set(new Set<string>());
    this.excludedKeys.set(new Set<string>());
    this.allMatching.set(false);
    this.scopes.set(new Array<GridSelectionScope>());
    this.excludedScopes.set(new Array<GridSelectionScope>());
    this.rowCache.set(new Map<string, GridRow>());
    this.anchors.clear();
  }

  /**
   * Materialised selected rows, preferring visible rows then the cache.
   *
   * @param visibleRows - Rows currently on screen.
   * @param path - Optional leaf path so allMatching exclusions honour group scopes.
   * @returns Selected row objects.
   */
  private materialisedRows(visibleRows: ReadonlyArray<GridRow>, path?: string): Array<GridRow> {
    const seen: Set<string> = new Set<string>();
    const result: Array<GridRow> = new Array<GridRow>();
    for (const row of visibleRows) {
      const key: string | null = this.keyOf(row);
      if (key === null || !this.isSelected(key, path) || seen.has(key)) {
        continue;
      }
      seen.add(key);
      result.push(row);
    }
    for (const [key, row] of this.rowCache()) {
      if (seen.has(key) || !this.includedKeys().has(key) || this.excludedKeys().has(key)) {
        continue;
      }
      seen.add(key);
      result.push(row);
    }
    return result;
  }

  /**
   * Toggles a row between inclusion, exclusion, and covering-mode exceptions.
   *
   * @param row - Data row.
   * @param path - Leaf path.
   */
  private toggleExclusionOrInclusion(row: GridRow, path: string): void {
    const key: string | null = this.keyOf(row);
    if (key === null) {
      return;
    }
    this.cacheRow(key, row);
    if (this.isCoveredSelection(path)) {
      const nextExcluded: Set<string> = new Set<string>(this.excludedKeys());
      if (nextExcluded.has(key)) {
        nextExcluded.delete(key);
      } else {
        nextExcluded.add(key);
      }
      this.excludedKeys.set(nextExcluded);
      return;
    }
    const nextIncluded: Set<string> = new Set<string>(this.includedKeys());
    if (nextIncluded.has(key)) {
      nextIncluded.delete(key);
    } else {
      nextIncluded.add(key);
    }
    this.includedKeys.set(nextIncluded);
  }

  /**
   * Whether allMatching or a covering scope currently owns this path.
   *
   * @param path - Leaf or group path.
   * @returns True when the path is covered wholesale.
   */
  private isCoveredSelection(path: string): boolean {
    if (this.isPathInScopes(path)) {
      return true;
    }
    if (this.allMatching()) {
      return !this.isPathInExcludedScopes(path);
    }
    return false;
  }

  /**
   * Toggles a group while every matching row is selected.
   *
   * Unchecking adds an excluded scope so sibling groups stay selected. Checking
   * an already-excluded group removes that exclusion. Checking a nested group
   * under an excluded ancestor re-selects that subtree as an included scope.
   *
   * @param scope - Group path, field, and estimated count.
   * @param loadedRows - Currently loaded rows in the group.
   */
  private toggleScopeWhileAllMatching(scope: GridSelectionScope, loadedRows: ReadonlyArray<GridRow>): void {
    if (this.hasScope(scope.path)) {
      this.removeCoveringScopes(scope.path);
      return;
    }
    if (this.hasExcludedScope(scope.path)) {
      this.removeExactExcludedScope(scope.path);
      this.includedKeys.set(this.pruneKeysForRows(this.includedKeys(), loadedRows));
      return;
    }
    if (this.isPathInExcludedScopes(scope.path)) {
      this.addIncludedScope(scope, loadedRows);
      return;
    }
    this.addExcludedScope(scope, loadedRows);
  }

  /**
   * Adds a wholesale group exclusion under allMatching.
   *
   * @param scope - Group to exclude.
   * @param loadedRows - Loaded rows whose per-row exceptions should be pruned.
   */
  private addExcludedScope(scope: GridSelectionScope, loadedRows: ReadonlyArray<GridRow>): void {
    const nextExcludedScopes: Array<GridSelectionScope> = this.excludedScopes().filter(
      (existing: GridSelectionScope): boolean => !isPathCoveredByScope(existing.path, scope.path)
    );
    nextExcludedScopes.push(scope);
    this.excludedScopes.set(nextExcludedScopes);
    this.scopes.set(
      this.scopes().filter(
        (existing: GridSelectionScope): boolean => !isPathCoveredByScope(existing.path, scope.path)
      )
    );
    this.excludedKeys.set(this.pruneKeysForRows(this.excludedKeys(), loadedRows));
    this.includedKeys.set(this.pruneKeysForRows(this.includedKeys(), loadedRows));
  }

  /**
   * Re-selects a subtree that sits under an excluded ancestor.
   *
   * @param scope - Nested group to include.
   * @param loadedRows - Loaded rows whose per-row inclusions should be pruned.
   */
  private addIncludedScope(scope: GridSelectionScope, loadedRows: ReadonlyArray<GridRow>): void {
    const next: Array<GridSelectionScope> = this.scopes().filter(
      (existing: GridSelectionScope): boolean => !isPathCoveredByScope(existing.path, scope.path)
    );
    next.push(scope);
    this.scopes.set(next);
    this.includedKeys.set(this.pruneKeysForRows(this.includedKeys(), loadedRows));
  }

  /**
   * Removes the excluded scope with this exact path and redundant nested inclusions.
   *
   * @param path - Group path to restore to the allMatching selection.
   */
  private removeExactExcludedScope(path: string): void {
    this.excludedScopes.set(
      this.excludedScopes().filter((scope: GridSelectionScope): boolean => scope.path !== path)
    );
    this.scopes.set(
      this.scopes().filter((scope: GridSelectionScope): boolean => !isPathCoveredByScope(scope.path, path))
    );
  }

  /**
   * Path-aware tri-state while every matching row is selected.
   *
   * @param path - Flat sentinel or composite group path.
   * @param loadedRows - Rows currently rendered for this path.
   * @returns `none`, `some`, or `all`.
   */
  private allMatchingHeaderState(path: string, loadedRows: ReadonlyArray<GridRow>): GridHeaderSelectionState {
    const excludedWholesale: boolean = this.isPathInExcludedScopes(path) && !this.isPathInScopes(path);
    if (loadedRows.length > 0) {
      const selectedCount: number = this.countSelectedLoaded(path, loadedRows);
      if (selectedCount === 0) {
        if (excludedWholesale) {
          return 'none';
        }
        if (this.hasExcludedDescendantScope(path) || this.hasExclusionInRows(loadedRows)) {
          return 'some';
        }
        return 'all';
      }
      if (selectedCount === loadedRows.length) {
        return this.hasExcludedDescendantScope(path) ? 'some' : 'all';
      }
      return 'some';
    }
    if (excludedWholesale) {
      return 'none';
    }
    if (this.hasExcludedDescendantScope(path)) {
      return 'some';
    }
    return 'all';
  }

  /**
   * Counts currently selected loaded rows for a path.
   *
   * @param path - Leaf or group path.
   * @param loadedRows - Rows currently rendered for this path.
   * @returns Number of selected loaded rows.
   */
  private countSelectedLoaded(path: string, loadedRows: ReadonlyArray<GridRow>): number {
    let selectedCount: number = 0;
    for (const row of loadedRows) {
      const key: string | null = this.keyOf(row);
      if (key !== null && this.isSelected(key, path)) {
        selectedCount += 1;
      }
    }
    return selectedCount;
  }

  /**
   * Sum of estimated counts for the given scopes.
   *
   * @param scopes - Scopes to total.
   * @returns Combined estimated count.
   */
  private scopeCountTotal(scopes: ReadonlyArray<GridSelectionScope>): number {
    return scopes.reduce((sum: number, scope: GridSelectionScope): number => sum + scope.estimatedCount, 0);
  }

  /**
   * Whether any excluded scope covers `path`.
   *
   * @param path - Leaf or group path.
   * @returns True when an excluded scope matches.
   */
  private isPathInExcludedScopes(path: string): boolean {
    return this.excludedScopes().some((scope: GridSelectionScope): boolean =>
      isPathCoveredByScope(path, scope.path)
    );
  }

  /**
   * Whether an excluded scope exists for this exact path.
   *
   * @param path - Group path.
   * @returns True when this path is an excluded scope.
   */
  private hasExcludedScope(path: string): boolean {
    return this.excludedScopes().some((scope: GridSelectionScope): boolean => scope.path === path);
  }

  /**
   * Whether any excluded scope is nested under `path`.
   *
   * @param path - Ancestor group path.
   * @returns True when a descendant group is excluded.
   */
  private hasExcludedDescendantScope(path: string): boolean {
    return this.excludedScopes().some(
      (scope: GridSelectionScope): boolean => scope.path !== path && isPathCoveredByScope(scope.path, path)
    );
  }

  /**
   * Whether any selected scope covers `path`.
   *
   * @param path - Leaf or group path.
   * @returns True when a scope matches.
   */
  private isPathInScopes(path: string): boolean {
    return this.scopes().some((scope: GridSelectionScope): boolean => isPathCoveredByScope(path, scope.path));
  }

  /**
   * Whether a scope for this exact path is selected.
   *
   * @param path - Group path.
   * @returns True when selected.
   */
  private hasScope(path: string): boolean {
    return this.scopes().some((scope: GridSelectionScope): boolean => scope.path === path);
  }

  /**
   * Drops scopes that cover `path` (the path itself and ancestors).
   *
   * @param path - Group or leaf path.
   */
  private removeCoveringScopes(path: string): void {
    this.scopes.set(
      this.scopes().filter((scope: GridSelectionScope): boolean => !isPathCoveredByScope(path, scope.path))
    );
  }

  /**
   * Keys that were in the previous loaded window and are absent from the next
   * window, excluding the unloaded tail after a first-page reset. An empty next
   * window returns every previous-window key.
   *
   * @param previousRows - Loaded rows before the latest data input.
   * @param nextRows - Loaded rows after the latest data input.
   * @returns Keys that should be dropped from the explicit selection.
   */
  private collectStaleKeysFromLoadedWindow(
    previousRows: ReadonlyArray<GridRow>,
    nextRows: ReadonlyArray<GridRow>
  ): Set<string> {
    const nextKeys: Set<string> = new Set<string>();
    const nextKeyList: Array<string> = new Array<string>();
    for (const row of nextRows) {
      const key: string | null = this.keyOf(row);
      if (key === null) {
        continue;
      }
      nextKeys.add(key);
      nextKeyList.push(key);
    }

    const previousKeyList: Array<string> = new Array<string>();
    for (const row of previousRows) {
      const key: string | null = this.keyOf(row);
      if (key !== null) {
        previousKeyList.push(key);
      }
    }

    if (nextKeyList.length === 0) {
      return new Set<string>(previousKeyList);
    }

    const stale: Set<string> = new Set<string>();
    let nextIndex: number = 0;
    for (const previousKey of previousKeyList) {
      if (nextIndex >= nextKeyList.length) {
        break;
      }
      if (previousKey === nextKeyList[nextIndex]) {
        nextIndex += 1;
        continue;
      }
      if (nextKeys.has(previousKey)) {
        while (nextIndex < nextKeyList.length && nextKeyList[nextIndex] !== previousKey) {
          nextIndex += 1;
        }
        if (nextIndex < nextKeyList.length) {
          nextIndex += 1;
        }
        continue;
      }
      stale.add(previousKey);
    }
    return stale;
  }

  /**
   * Removes keys from inclusions, exclusions, and the row cache.
   *
   * @param keys - Keys that are no longer valid in the loaded window.
   */
  private dropKeys(keys: ReadonlySet<string>): void {
    this.includedKeys.set(
      new Set<string>([...this.includedKeys()].filter((key: string): boolean => !keys.has(key)))
    );
    this.excludedKeys.set(
      new Set<string>([...this.excludedKeys()].filter((key: string): boolean => !keys.has(key)))
    );
    const cache: Map<string, GridRow> = new Map<string, GridRow>(this.rowCache());
    for (const key of keys) {
      cache.delete(key);
    }
    this.rowCache.set(cache);
  }

  /**
   * Drops keys that belong to the given loaded rows.
   *
   * @param keys - Current key set.
   * @param rows - Rows whose keys should be removed.
   * @returns A new set without those keys.
   */
  private pruneKeysForRows(keys: ReadonlySet<string>, rows: ReadonlyArray<GridRow>): Set<string> {
    if (rows.length === 0 || keys.size === 0) {
      return new Set<string>(keys);
    }
    const remove: Set<string> = new Set<string>();
    for (const row of rows) {
      const key: string | null = this.keyOf(row);
      if (key !== null) {
        remove.add(key);
      }
    }
    return new Set<string>([...keys].filter((key: string): boolean => !remove.has(key)));
  }

  /**
   * Whether any loaded row is in `excludedKeys`.
   *
   * @param rows - Loaded rows.
   * @returns True when at least one is excluded.
   */
  private hasExclusionInRows(rows: ReadonlyArray<GridRow>): boolean {
    const excluded: ReadonlySet<string> = this.excludedKeys();
    if (excluded.size === 0) {
      return false;
    }
    for (const row of rows) {
      const key: string | null = this.keyOf(row);
      if (key !== null && excluded.has(key)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Keeps a materialised row in the cache.
   *
   * @param key - Row key.
   * @param row - Data row.
   */
  private cacheRow(key: string, row: GridRow): void {
    const cache: Map<string, GridRow> = new Map<string, GridRow>(this.rowCache());
    cache.set(key, row);
    this.rowCache.set(cache);
  }

  /**
   * Drops covering modes and keeps at most one included key.
   */
  private collapseToSingle(): void {
    this.allMatching.set(false);
    this.scopes.set(new Array<GridSelectionScope>());
    this.excludedScopes.set(new Array<GridSelectionScope>());
    this.excludedKeys.set(new Set<string>());
    const keys: Array<string> = [...this.includedKeys()];
    if (keys.length <= 1) {
      return;
    }
    this.includedKeys.set(new Set<string>([keys[keys.length - 1]]));
  }
}
